import React from "react";
import {
  Button,
  ButtonGroup,
  HStack,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import {
  FiAlignCenter,
  FiAlignJustify,
  FiAlignRight,
  FiBold,
  FiCornerDownLeft,
  FiCornerDownRight,
  FiItalic,
  FiLink,
  FiList,
  FiMenu,
  FiMinus,
  FiType,
} from "react-icons/fi";
import { getNewsContentHtml } from "../../utils/news";
import "./TiptapEditor.css";

interface TiptapEditorProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const TiptapEditor = ({
  value = "",
  onChange,
  placeholder = "",
}: TiptapEditorProps) => {
  const toolbarBg = useColorModeValue("gray.50", "gray.800");
  const editorBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: getNewsContentHtml(value),
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getHTML());
    },
  });

  React.useEffect(() => {
    if (!editor) return;

    const nextHtml = getNewsContentHtml(value);
    if (editor.getHTML() !== nextHtml) {
      editor.commands.setContent(nextHtml, { emitUpdate: false });
    }
  }, [editor, value]);

  const setLink = React.useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href || "";
    const url = window.prompt("آدرس لینک را وارد کنید", previousUrl);

    if (url === null) return;

    if (!url.trim()) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const toolbarButtons = [
    {
      label: "بولد",
      icon: FiBold,
      isActive: editor.isActive("bold"),
      onClick: () => editor.chain().focus().toggleBold().run(),
    },
    {
      label: "ایتالیک",
      icon: FiItalic,
      isActive: editor.isActive("italic"),
      onClick: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      label: "زیرخط",
      icon: FiMinus,
      isActive: editor.isActive("underline"),
      onClick: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      label: "تیتر",
      icon: FiType,
      isActive: editor.isActive("heading", { level: 2 }),
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      label: "فهرست",
      icon: FiList,
      isActive: editor.isActive("bulletList"),
      onClick: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      label: "فهرست عددی",
      icon: FiMenu,
      isActive: editor.isActive("orderedList"),
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      label: "لینک",
      icon: FiLink,
      isActive: editor.isActive("link"),
      onClick: setLink,
    },
    {
      label: "راست",
      icon: FiAlignRight,
      isActive: editor.isActive({ textAlign: "right" }),
      onClick: () => editor.chain().focus().setTextAlign("right").run(),
    },
    {
      label: "وسط",
      icon: FiAlignCenter,
      isActive: editor.isActive({ textAlign: "center" }),
      onClick: () => editor.chain().focus().setTextAlign("center").run(),
    },
    {
      label: "چپ",
      icon: FiAlignJustify,
      isActive: editor.isActive({ textAlign: "left" }),
      onClick: () => editor.chain().focus().setTextAlign("left").run(),
    },
  ];

  return (
    <div
      className="tiptap-editor"
      style={{
        background: editorBg,
        borderColor,
      }}
    >
      <HStack
        className="tiptap-editor__toolbar"
        align="start"
        spacing={2}
        style={{ background: toolbarBg, borderColor }}
      >
        <ButtonGroup isAttached={false} size="sm" flexWrap="wrap">
          {toolbarButtons.map((button) => (
            <Button
              key={button.label}
              type="button"
              variant={button.isActive ? "solid" : "outline"}
              colorScheme={button.isActive ? "brand" : "gray"}
              onClick={button.onClick}
              leftIcon={<Icon as={button.icon as any} />}
            >
              {button.label}
            </Button>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => editor.chain().focus().undo().run()}
            leftIcon={<Icon as={FiCornerDownRight as any} />}
          >
            واگرد
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => editor.chain().focus().redo().run()}
            leftIcon={<Icon as={FiCornerDownLeft as any} />}
          >
            بازانجام
          </Button>
        </ButtonGroup>
      </HStack>
      <div className="tiptap-editor__content">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};
